import React, { useMemo, useEffect } from 'react';
import { Filter2 } from '@piiqtechnologies/svg-icons';
import { PeekClickAwayListener } from './PeekClickAwayListener.js';
import { PeekPopper } from './PeekPopper.js';
import { PeekIconButton } from './PeekIconButton.js';
import { PeekPaper } from './PeekPaper.js';
import { PeekList } from './PeekList.js';
import { PeekListItem } from './PeekListItem.js';
import { PeekListItemButton } from './PeekListItemButton.js';
import { PeekListItemIcon } from './PeekListItemIcon.js';
import { PeekListItemText } from './PeekListItemText.js';
import { PeekCheckbox } from './PeekCheckbox.js';
import '../themes/theme.js';
import { PeekStack } from './PeekStack.js';
import { PeekTextField } from './PeekTextField.js';
import { useTheme } from '@mui/material/styles';

const PeekTableHeadFilter = (props) => {
    const theme = useTheme();
    const { filters, defaultFilters, onFilterChange, rangeFilter } = props;
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const [selectedFilters, setSelectedFilters] = React.useState([]);
    const active = !!(selectedFilters === null || selectedFilters === void 0 ? void 0 : selectedFilters.length);
    const handleFilterSelection = (value) => () => {
        const currentIndex = selectedFilters.indexOf(value);
        const newSelectedFilters = [...selectedFilters];
        if (currentIndex === -1) {
            newSelectedFilters.push(value);
        }
        else {
            newSelectedFilters.splice(currentIndex, 1);
        }
        setSelectedFilters(newSelectedFilters);
        onFilterChange && onFilterChange(newSelectedFilters);
    };
    const toggleFilter = (event) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };
    const [minRannge, maxRange] = useMemo(() => {
        if (rangeFilter && filters.length > 0) {
            const validFilters = filters.map((f) => parseFloat(f.id)).filter((f) => !isNaN(f));
            const min = Math.min(...validFilters);
            const max = Math.max(...validFilters);
            return [min, max];
        }
        return [];
    }, [filters, rangeFilter]);
    useEffect(() => {
        defaultFilters && setSelectedFilters(defaultFilters);
    }, [defaultFilters]);
    return (React.createElement(React.Fragment, null,
        React.createElement(PeekIconButton, { id: "filter-button", "aria-controls": open ? "filter-menu" : undefined, "aria-haspopup": "true", "aria-expanded": open ? "true" : undefined, "aria-label": "Show Filter Options", onClick: toggleFilter, bordered: false },
            React.createElement(Filter2, { color: (active && theme.palette.secondary.main) || theme.palette.primary.main })),
        React.createElement(PeekPopper, { id: "filter-menu", "aria-labelledby": "filter-button", anchorEl: anchorEl, open: open, role: undefined, placement: "bottom-end", sx: { zIndex: 9999 } },
            React.createElement(PeekClickAwayListener, { onClickAway: toggleFilter },
                React.createElement(PeekPaper, { elevation: 3, sx: { mt: 1 } }, (props.rangeFilter && (React.createElement(PeekStack, { direction: "row", justifyContent: "center", alignItems: "center", justifyItems: "center", spacing: 1, sx: { p: 1 } },
                    React.createElement(PeekTextField, { label: "Min", variant: "standard", size: "small", type: "number", placeholder: `${minRannge || "Min"}`, value: selectedFilters[0] || "", onChange: (e) => {
                            const newFilters = [...selectedFilters];
                            newFilters[0] = e.target.value;
                            setSelectedFilters(newFilters);
                            onFilterChange && onFilterChange(newFilters);
                        }, sx: { width: 80 }, InputLabelProps: { shrink: true }, InputProps: {
                            disableUnderline: true,
                        }, inputProps: {
                            min: minRannge || 0,
                            max: maxRange || Infinity,
                        } }),
                    React.createElement(PeekTextField, { label: "Max", variant: "standard", size: "small", type: "number", placeholder: `${maxRange || "Max"}`, value: selectedFilters[1] || "", onChange: (e) => {
                            const newFilters = [...selectedFilters];
                            newFilters[1] = e.target.value;
                            setSelectedFilters(newFilters);
                            onFilterChange && onFilterChange(newFilters);
                        }, sx: { width: 80 }, InputLabelProps: { shrink: true }, InputProps: {
                            disableUnderline: true,
                        }, inputProps: {
                            min: minRannge || 0,
                            max: maxRange || Infinity,
                        } })))) || (React.createElement(PeekList, { disablePadding: true, sx: { width: 130, maxHeight: 300, overflowY: "auto", py: 1 } }, filters.map(({ id, label }) => {
                    const labelId = `checkbox-list-label-${id}`;
                    return (React.createElement(PeekListItem, { key: id, disablePadding: true },
                        React.createElement(PeekListItemButton, { role: undefined, sx: { py: 0 }, onClick: handleFilterSelection(id), dense: true },
                            React.createElement(PeekListItemIcon, null,
                                React.createElement(PeekCheckbox, { edge: "start", checked: selectedFilters.indexOf(id) !== -1, tabIndex: -1, color: "success", disableRipple: true, inputProps: { "aria-labelledby": labelId }, sx: { py: 0.5 } })),
                            React.createElement(PeekListItemText, { id: labelId, primary: label, primaryTypographyProps: { variant: "body1" } }))));
                }))))))));
};

export { PeekTableHeadFilter, PeekTableHeadFilter as default };
